import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { gradientBrand, passtrackColors } from "../theme";
import { useAuth } from "../routing/AuthContext";
import { EntryShell, entryCardSx } from "../routing/entryShell";
import { ROUTES } from "../routing/paths";

const primaryButtonSx = {
  py: 1.25,
  fontWeight: 700,
  backgroundImage: gradientBrand,
  "&:hover": {
    backgroundImage: gradientBrand,
    filter: "brightness(0.95)",
  },
} as const;

function ArrowLeftIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M19 12H5" />
      <path d="M12 19l-7-7 7-7" />
    </svg>
  );
}

export default function SelectPropertyPage() {
  const navigate = useNavigate();
  const { properties } = useAuth();
  const [selectedDocumentId, setSelectedDocumentId] = useState("");

  const selectableProperties = properties.filter(
    (property) => property.documentId,
  );

  const selectedProperty =
    selectableProperties.find(
      (property) => property.documentId === selectedDocumentId,
    ) ?? null;

  if (properties.length === 0) {
    navigate(ROUTES.setupNew, { replace: true });
    return null;
  }

  return (
    <EntryShell>
      <Card variant="outlined" sx={entryCardSx}>
        <Box
          component="button"
          type="button"
          onClick={() => navigate(ROUTES.menu)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            mb: 3,
            background: "none",
            border: "none",
            color: passtrackColors.textMuted,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            p: 0,
            font: "inherit",
          }}
        >
          <ArrowLeftIcon />
          Volver
        </Box>

        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: passtrackColors.blue,
            mb: 1,
          }}
        >
          Editar propiedad
        </Typography>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 24,
            color: passtrackColors.ink,
            mb: 1,
          }}
        >
          Selecciona una propiedad
        </Typography>
        <Typography
          sx={{ fontSize: 14, color: passtrackColors.textMuted, mb: 3 }}
        >
          Elige la propiedad que deseas editar para continuar.
        </Typography>

        {selectableProperties.length === 0 ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Tus propiedades no tienen identificador disponible para editar.
          </Alert>
        ) : (
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="property-select-label">Propiedad</InputLabel>
            <Select
              labelId="property-select-label"
              label="Propiedad"
              value={selectedDocumentId}
              onChange={(event) => setSelectedDocumentId(event.target.value)}
            >
              {selectableProperties.map((property) => (
                <MenuItem
                  key={property.documentId}
                  value={property.documentId}
                >
                  {property.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {selectedProperty?.address ? (
          <Typography
            sx={{ fontSize: 13, color: passtrackColors.textMuted, mb: 2 }}
          >
            {selectedProperty.address}
          </Typography>
        ) : null}

        <Button
          variant="contained"
          fullWidth
          disabled={!selectedProperty?.documentId}
          onClick={() => {
            if (selectedProperty?.documentId) {
              navigate(ROUTES.setupEdit(selectedProperty.documentId));
            }
          }}
          sx={primaryButtonSx}
        >
          Continuar
        </Button>
      </Card>
    </EntryShell>
  );
}
